import { useEffect, useId, useRef } from 'react'
import { createPortal } from 'react-dom'
import type { ReactNode } from 'react'
import { cn } from './cn'

export interface ModalProps {
  open: boolean
  onClose?: () => void
  title?: ReactNode
  children?: ReactNode
  className?: string
}

export function Modal({ open, onClose, title, children, className = '' }: ModalProps) {
  const overlayRef = useRef<HTMLDivElement | null>(null)
  const panelRef = useRef<HTMLDivElement | null>(null)
  const onCloseRef = useRef(onClose)
  const previousActiveElementRef = useRef<HTMLElement | null>(null)
  const titleId = useId()
  onCloseRef.current = onClose

  useEffect(() => {
    if (!open) return
    const getFocusableElements = (): HTMLElement[] => {
      const panel = panelRef.current
      if (!panel) return []
      return Array.from(
        panel.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ),
      )
    }

    const originalOverflow = document.body.style.overflow
    const handleKeydown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onCloseRef.current?.()
        return
      }

      if (e.key !== 'Tab' || !panelRef.current) return

      const focusable = getFocusableElements()
      if (focusable.length === 0) {
        e.preventDefault()
        panelRef.current.focus()
        return
      }

      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      const active = document.activeElement as HTMLElement | null

      if (e.shiftKey) {
        if (!active || active === first || !panelRef.current.contains(active)) {
          e.preventDefault()
          last.focus()
        }
        return
      }

      if (active === last) {
        e.preventDefault()
        first.focus()
      }
    }

    document.addEventListener('keydown', handleKeydown)
    document.body.style.overflow = 'hidden'
    previousActiveElementRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null
    queueMicrotask(() => {
      const focusable = getFocusableElements()
      if (focusable.length > 0) {
        focusable[0].focus()
        return
      }
      panelRef.current?.focus()
    })
    return () => {
      document.removeEventListener('keydown', handleKeydown)
      document.body.style.overflow = originalOverflow
      queueMicrotask(() => {
        previousActiveElementRef.current?.focus()
      })
    }
  }, [open])

  if (!open || typeof document === 'undefined') return null

  return createPortal(
    <div
      ref={overlayRef}
      className="fixed inset-0 z-modal flex items-start justify-center overflow-y-auto bg-ink-deep/55 px-4 py-4 sm:py-6 animate-fade-in motion-reduce:animate-none"
      onClick={(e) => { if (e.target === overlayRef.current) onClose?.() }}
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? titleId : undefined}
    >
      <div
        ref={panelRef}
        tabIndex={-1}
        className={cn('my-0 flex w-full max-w-lg max-h-[calc(100dvh-2rem)] flex-col overflow-hidden rounded-overlay bg-white shadow-3 animate-fade-up motion-reduce:animate-none', className)}
      >
        {title && (
          <div className="flex items-center justify-between px-6 py-4 border-b border-line">
            <h2 id={titleId} className="text-lg font-display text-strong">{title}</h2>
            <button
              type="button"
              onClick={onClose}
              className="inline-flex items-center justify-center p-1.5 text-ink-muted hover:text-strong hover:bg-whisper rounded-control transition-colors focus-visible:outline-none focus-visible:shadow-focus coarse:min-h-tap coarse:min-w-tap"
              aria-label="Close"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        )}
        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-4">
          {children}
        </div>
      </div>
    </div>,
    document.body,
  )
}
